import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Breadcrumbs,
  Link as MuiLink,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Visibility as ViewIcon,
  Schedule as DurationIcon,
  LocationOn as LocationIcon,
  Share as ShareIcon,
  ArrowBack as BackIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  DirectionsCar as TransportIcon,
  Event as EventIcon,
  LocalActivity as ActivityIcon,
  Place as PlaceIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Lightbulb as TipsIcon,
  Send as SendIcon,
  NavigateNext as NavNextIcon,
  FlightTakeoff as TripIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UnsplashAttribution from '../components/UnsplashAttribution';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import CreateTripFromGuideDialog from '../components/CreateTripFromGuideDialog';
import api from '../config/api';
import { THEME_COLORS } from '../constants/theme';

const categoryIcons = {
  hotel: <HotelIcon />,
  restaurant: <RestaurantIcon />,
  event: <EventIcon />,
  transport: <TransportIcon />,
  activity: <ActivityIcon />,
  other: <PlaceIcon />
};

const categoryColors = {
  hotel: '#e65100',
  restaurant: '#2e7d32',
  event: '#7b1fa2',
  transport: '#1565c0',
  activity: '#f57f17',
  other: '#616161'
};

const travelStyleColors = {
  budget: '#4caf50',
  'mid-range': '#2196f3',
  luxury: '#9c27b0',
  backpacker: '#ff9800',
  family: '#e91e63',
  adventure: '#f44336',
  cultural: '#795548',
  relaxation: '#00bcd4',
};

const TravelGuideDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [comment, setComment] = useState('');
  const [commenting, setCommenting] = useState(false);
  const [commentAnonymous, setCommentAnonymous] = useState(false);
  const [createTripOpen, setCreateTripOpen] = useState(false);

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('token');
      if (!token || !user) return;
      try {
        const response = await api.get('/forum/admin/check');
        setIsAdmin(response.data.isAdmin);
      } catch (err) {
        // Not admin
      }
    };
    checkAdmin();
  }, [user]);

  const handleAdminDelete = async () => {
    if (!guide) return;
    setDeleting(true);
    try {
      await api.delete(`/guides/admin/${guide.id}`);
      setSnackbar({ open: true, message: t('guides.deleteSuccess', 'Guide deleted successfully'), severity: 'success' });
      setDeleteDialogOpen(false);
      setTimeout(() => navigate('/guides'), 1500);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || t('guides.deleteFailed', 'Failed to delete guide'), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const fetchGuide = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/guides/${slug}`);
      if (response.data.success) {
        setGuide(response.data.guide);
        setLiked(response.data.guide.user_liked);
        setLikeCount(response.data.guide.like_count);
      }
    } catch (err) {
      console.error('Error fetching guide:', err);
      setError(err.response?.status === 404 ? t('guides.notFound') : t('guides.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    fetchGuide();
  }, [fetchGuide]);

  const handleLike = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      const response = await api.post(`/guides/${guide.id}/like`);
      setLiked(response.data.liked);
      setLikeCount(response.data.likeCount);
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  const handleComment = async () => {
    if (!comment.trim() || !user) return;
    
    setCommenting(true);
    try {
      const response = await api.post(`/guides/${guide.id}/comment`, { content: comment.trim(), isAnonymous: commentAnonymous });
      if (response.data.success) {
        setGuide(prev => ({
          ...prev,
          comments: [response.data.comment, ...(prev.comments || [])]
        }));
        setComment('');
        setCommentAnonymous(false);
      }
    } catch (err) {
      console.error('Comment error:', err);
    } finally {
      setCommenting(false);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/guides/${slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: guide.title, url });
      } catch (err) { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${ampm}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !guide) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
        <Alert severity="error">{error || t('guides.notFound')}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/guides')} sx={{ mt: 2, textTransform: 'none' }}>
          {t('guides.backToGuides')}
        </Button>
      </Container>
    );
  }

  const dayNumbers = guide.items ? Object.keys(guide.items).sort((a, b) => parseInt(a) - parseInt(b)) : [];

  // Structured data for SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": guide.title,
    "description": guide.description || `${guide.duration_days}-day travel guide to ${guide.destination}`,
    "url": `https://dejavunomad.com/guides/${slug}`,
    ...(guide.cover_image && { "image": guide.cover_image }),
    "datePublished": guide.published_at || guide.created_at,
    "dateModified": guide.updated_at || guide.published_at,
    "author": guide.is_anonymous ? {
      "@type": "Organization",
      "name": "Déjà Vu Nomad Community"
    } : {
      "@type": "Person",
      "name": guide.author_name || "Déjà Vu Nomad User"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Déjà Vu Nomad",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dejavunomad.com/noBgColor.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://dejavunomad.com/guides/${slug}`
    },
    "about": {
      "@type": "Place",
      "name": guide.destination
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dejavunomad.com" },
      { "@type": "ListItem", "position": 2, "name": "Travel Guides", "item": "https://dejavunomad.com/guides" },
      { "@type": "ListItem", "position": 3, "name": guide.title, "item": `https://dejavunomad.com/guides/${slug}` }
    ]
  };

  return (
    <>
      <SEOHead
        title={`${guide.title} | ${t('guides.pageTitle')} | Déjà Vu Nomad`}
        description={guide.description || `${guide.duration_days}-day travel guide to ${guide.destination}`}
        canonical={`https://dejavunomad.com/guides/${slug}`}
        ogImage={guide.cover_image || '/noBgColor.png'}
        ogType="article"
        keywords={`${guide.destination}, travel guide, ${guide.travel_style || ''}, ${guide.duration_days}-day itinerary, ${guide.destination} things to do, trip planner`}
        structuredData={[articleSchema, breadcrumbSchema]}
        publishedTime={guide.published_at || guide.created_at}
        modifiedTime={guide.updated_at}
      />

      {/* Hero / Header */}
      <Box sx={{
        position: 'relative',
        background: guide.cover_image 
          ? `linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.8)), url(${guide.cover_image}) center/cover`
          : `linear-gradient(135deg, ${THEME_COLORS.primary.main} 0%, ${THEME_COLORS.secondary.main} 100%)`,
        color: 'white',
        py: { xs: 3, md: 4 },
      }}>
        {/* Unsplash Attribution */}
        <UnsplashAttribution
          imageUrl={guide.cover_image}
          photographerName={guide.unsplash_photographer_name}
          photographerUsername={guide.unsplash_photographer_username}
          variant="overlay"
        />
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavNextIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />}
            sx={{ mb: 2, '& .MuiBreadcrumbs-li': { color: 'rgba(255,255,255,0.7)' } }}
          >
            <MuiLink component={Link} to="/" color="inherit" underline="hover" sx={{ fontSize: '0.8rem' }}>
              {t('nav.home')}
            </MuiLink>
            <MuiLink component={Link} to="/guides" color="inherit" underline="hover" sx={{ fontSize: '0.8rem' }}>
              {t('guides.pageTitle')}
            </MuiLink>
            <Typography sx={{ fontSize: '0.8rem', color: 'white' }}>
              {guide.title}
            </Typography>
          </Breadcrumbs>

          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            mb: 1,
            fontSize: { xs: '1.5rem', md: '2.2rem' }
          }}>
            {guide.title}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            {guide.destination && (
              <Chip 
                icon={<LocationIcon sx={{ color: 'white !important', fontSize: 16 }} />}
                label={guide.destination}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
              />
            )}
            <Chip
              icon={<DurationIcon sx={{ color: 'white !important', fontSize: 16 }} />}
              label={`${guide.duration_days} ${guide.duration_days === 1 ? t('guides.day') : t('guides.days')}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
            />
            {guide.travel_style && (
              <Chip
                label={t(`guides.style.${guide.travel_style}`, guide.travel_style)}
                sx={{ 
                  bgcolor: travelStyleColors[guide.travel_style] || '#666', 
                  color: 'white', 
                  fontWeight: 600 
                }}
              />
            )}
          </Box>

          {/* Author & Stats Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar 
                src={guide.author?.avatar} 
                sx={{ width: 36, height: 36, bgcolor: THEME_COLORS.primary.main }}
              >
                {guide.author?.name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {guide.author?.name}
                </Typography>
                {guide.published_at && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {t('guides.publishedOn')} {new Date(guide.published_at).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ViewIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2">{guide.view_count.toLocaleString()}</Typography>
              </Box>
              <Button
                size="small"
                startIcon={liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                onClick={handleLike}
                sx={{ 
                  color: 'white', 
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                }}
              >
                {likeCount}
              </Button>
              <IconButton onClick={handleShare} sx={{ color: 'white' }}>
                <ShareIcon />
              </IconButton>
              {isAdmin && (
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(211,47,47,0.7)',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 1.5,
                    '&:hover': { bgcolor: 'rgba(211,47,47,0.9)' }
                  }}
                >
                  {t('guides.adminDelete', 'Delete Guide')}
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {/* Description */}
            {guide.description && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                  {guide.description}
                </Typography>
              </Paper>
            )}

            {/* Day-by-day Itinerary */}
            {dayNumbers.map(dayNum => (
              <Box key={dayNum} sx={{ mb: 3 }}>
                <Paper sx={{ 
                  p: 2, 
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  {/* Day Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    mb: 2,
                    pb: 1.5,
                    borderBottom: '2px solid',
                    borderColor: 'primary.main'
                  }}>
                    <Box sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      flexShrink: 0,
                    }}>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('guides.dayLabel')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, lineHeight: 1 }}>
                        {dayNum}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {t('guides.dayTitle', { day: dayNum })}
                    </Typography>
                    <Chip 
                      label={`${guide.items[dayNum].length} ${t('guides.activities')}`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 'auto', fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </Box>

                  {/* Day Items */}
                  {guide.items[dayNum].map((item, idx) => (
                    <Box 
                      key={item.id}
                      sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        py: 1.5,
                        px: 1,
                        borderBottom: idx < guide.items[dayNum].length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'grey.50', borderRadius: 2 },
                        transition: 'background 0.2s'
                      }}
                    >
                      {/* Category Icon */}
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: `${categoryColors[item.category] || '#666'}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: categoryColors[item.category] || '#666',
                        '& .MuiSvgIcon-root': { fontSize: 20 }
                      }}>
                        {categoryIcons[item.category] || categoryIcons.other}
                      </Box>

                      {/* Item Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
                          {item.name}
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
                          {item.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <LocationIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">{item.location}</Typography>
                            </Box>
                          )}
                          {item.start_time && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <TimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatTime(item.start_time)}
                                {item.end_time && ` - ${formatTime(item.end_time)}`}
                              </Typography>
                            </Box>
                          )}
                          {item.cost > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <MoneyIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {item.cost_currency === 'USD' ? '$' : item.cost_currency}{parseFloat(item.cost).toFixed(0)}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {item.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                            {item.description}
                          </Typography>
                        )}

                        {item.tips && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: 0.5, 
                            mt: 0.5,
                            p: 1,
                            bgcolor: '#fff8e1',
                            borderRadius: 1.5,
                            border: '1px solid #fff3c4'
                          }}>
                            <TipsIcon sx={{ fontSize: 14, color: '#f9a825', mt: 0.2 }} />
                            <Typography variant="caption" sx={{ color: '#6d4c00' }}>
                              {item.tips}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Paper>
              </Box>
            ))}

            {dayNumbers.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {t('guides.noItineraryItems')}
              </Alert>
            )}

            {/* Comments Section */}
            <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {t('guides.comments')} ({guide.comments?.length || 0})
              </Typography>

              {/* Comment Input */}
              {user ? (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('guides.addComment')}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleComment()}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <IconButton 
                      onClick={handleComment} 
                      disabled={commenting || !comment.trim()}
                      color="primary"
                    >
                      {commenting ? <CircularProgress size={20} /> : <SendIcon />}
                    </IconButton>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={commentAnonymous}
                        onChange={(e) => setCommentAnonymous(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="caption">{t('common.postAnonymously')}</Typography>}
                    sx={{ mt: 0.5, ml: 0 }}
                  />
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  <MuiLink component={Link} to="/login">{t('guides.loginToComment')}</MuiLink>
                </Alert>
              )}

              {/* Comments List */}
              <List disablePadding>
                {(guide.comments || []).map((c, idx) => (
                  <React.Fragment key={c.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar src={c.author?.avatar} sx={{ width: 32, height: 32, bgcolor: THEME_COLORS.secondary.main }}>
                          {c.author?.name?.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {c.author?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(c.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                        secondary={c.content}
                      />
                    </ListItem>
                    {idx < guide.comments.length - 1 && <Divider variant="inset" />}
                  </React.Fragment>
                ))}
              </List>

              {(!guide.comments || guide.comments.length === 0) && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  {t('guides.noComments')}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Quick Info Card */}
            <Paper sx={{ p: 2.5, borderRadius: 3, mb: 2, position: 'sticky', top: 80 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                {t('guides.quickInfo')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {guide.destination && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{guide.destination}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DurationIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {guide.duration_days} {guide.duration_days === 1 ? t('guides.day') : t('guides.days')}
                  </Typography>
                </Box>
                {guide.budget_estimate > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MoneyIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      ~{guide.budget_currency === 'USD' ? '$' : guide.budget_currency}{parseFloat(guide.budget_estimate).toLocaleString()} {t('guides.estimated')}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Tags */}
              {guide.tags && guide.tags.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, display: 'block' }}>
                    {t('guides.tags')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {guide.tags.map((tag, i) => (
                      <Chip key={i} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Action Buttons */}
              <Button
                fullWidth
                variant={liked ? 'contained' : 'outlined'}
                startIcon={liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                onClick={handleLike}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, mb: 1 }}
              >
                {liked ? t('guides.liked') : t('guides.likeGuide')} ({likeCount})
              </Button>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleShare}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, mb: 1 }}
              >
                {t('guides.shareGuide')}
              </Button>

              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<TripIcon />}
                onClick={() => {
                  if (!user) {
                    navigate('/login');
                    return;
                  }
                  setCreateTripOpen(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('guides.useThisGuide', 'Use This Guide')}
              </Button>
            </Paper>

            {/* Author Card */}
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                {t('guides.aboutAuthor')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar src={guide.author?.avatar} sx={{ width: 48, height: 48, bgcolor: THEME_COLORS.primary.main }}>
                  {guide.author?.name?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{guide.author?.name}</Typography>
                  {guide.author?.bio && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {guide.author.bio}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Create Trip from Guide Dialog */}
      {guide && (
        <CreateTripFromGuideDialog
          open={createTripOpen}
          onClose={() => setCreateTripOpen(false)}
          guide={guide}
        />
      )}

      {/* Admin Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('guides.confirmDeleteTitle', 'Delete Travel Guide')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('guides.confirmDeleteMessage', 'Are you sure you want to delete this guide? This action cannot be undone. All comments, likes, and itinerary items will be permanently removed.')}
          </DialogContentText>
          {guide && (
            <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, color: 'text.primary' }}>
              "{guide.title}"
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleAdminDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {deleting ? t('common.deleting', 'Deleting...') : t('guides.adminDelete', 'Delete Guide')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TravelGuideDetail;
